import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { EAPlayerStatsCard } from '../components/EAPlayerStatsCard';
import {
  User,
  Camera,
  Upload,
  Trash2,
  Gamepad2,
  Mail,
  Phone,
  Calendar,
  Shield,
  Coins,
  Pencil,
  X,
  AlertTriangle,
} from 'lucide-react';

const GAMERTAG_COST = 200000;
const PICTURE_CHANGE_COST = 100000;

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showGamertagDialog, setShowGamertagDialog] = useState(false);
  const [newGamertag, setNewGamertag] = useState('');
  const [changingGamertag, setChangingGamertag] = useState(false);
  const [pictureChangeCount, setPictureChangeCount] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);
  const [showPictureCostDialog, setShowPictureCostDialog] = useState(false);
  const fileInputRef = useRef(null);

  const isItalian = i18n.language === 'it';

  // Discord link: handle redirect back from OAuth + actions
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get('discord');
    if (d) {
      window.history.replaceState(null, '', window.location.pathname);
      if (d === 'linked') {
        toast.success(isItalian ? 'Discord collegato con successo!' : 'Discord linked successfully!');
        refreshUser?.();
      } else if (d === 'already_linked') {
        toast.error(isItalian ? 'Questo account Discord è già collegato a un altro utente' : 'This Discord account is already linked to another user');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkDiscord = async () => {
    try {
      const res = await authApi.discordLinkUrl();
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isItalian ? 'Login Discord non configurato' : 'Discord login not configured'));
    }
  };

  const handleUnlinkDiscord = async () => {
    if (!window.confirm(isItalian ? 'Scollegare Discord dal tuo account?' : 'Unlink Discord from your account?')) return;
    try {
      await authApi.discordUnlink();
      toast.success(isItalian ? 'Discord scollegato' : 'Discord unlinked');
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  // Fetch picture change count on mount
  React.useEffect(() => {
    authApi.getProfilePictureCost().then(res => {
      setPictureChangeCount(res.data.change_count || 0);
    }).catch(() => {});
  }, []);

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const res = await authApi.uploadProfilePicture(file);
      const msg = res.data?.message || (isItalian ? 'Immagine profilo aggiornata!' : 'Profile picture updated!');
      toast.success(msg);
      setPictureChangeCount(prev => prev + 1);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nel caricamento' : 'Upload failed'));
    } finally {
      setUploading(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(isItalian ? 'Tipo file non supportato. Usa JPG, PNG, GIF o WEBP.' : 'Unsupported file type.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(isItalian ? 'Immagine troppo grande. Massimo 2MB.' : 'Image too large. Max 2MB.');
      return;
    }
    // If already changed once, show cost confirmation
    if (pictureChangeCount >= 1) {
      setPendingFile(file);
      setShowPictureCostDialog(true);
    } else {
      await doUpload(file);
    }
  };

  const handleRemovePicture = async () => {
    setRemoving(true);
    try {
      await authApi.deleteProfilePicture();
      toast.success(isItalian ? 'Immagine profilo rimossa!' : 'Profile picture removed!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella rimozione' : 'Remove failed'));
    } finally {
      setRemoving(false);
    }
  };

  const handleChangeGamertag = async () => {
    const gt = newGamertag.trim();
    if (!gt || gt.length < 3) {
      toast.error(isItalian ? 'Il gamertag deve avere almeno 3 caratteri' : 'Gamertag must be at least 3 characters');
      return;
    }
    setChangingGamertag(true);
    try {
      const res = await authApi.changeGamertag(gt);
      toast.success(res.data.message);
      setShowGamertagDialog(false);
      setNewGamertag('');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nel cambio gamertag' : 'Failed to change gamertag'));
    } finally {
      setChangingGamertag(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { bg: 'bg-[#FFEA00]/20', text: 'text-[#FFEA00]', border: 'border-[#FFEA00]/30', label: 'Super Admin' },
      admin: { bg: 'bg-[#D500F9]/20', text: 'text-[#D500F9]', border: 'border-[#D500F9]/30', label: 'Admin' },
      captain: { bg: 'bg-[#00F0FF]/20', text: 'text-[#00F0FF]', border: 'border-[#00F0FF]/30', label: isItalian ? 'Capitano' : 'Captain' },
      player: { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/30', label: isItalian ? 'Giocatore' : 'Player' },
    };
    return badges[role] || badges.player;
  };

  const roleBadge = getRoleBadge(user?.role);
  const canAfford = (user?.personal_coins || 0) >= GAMERTAG_COST;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
        <User size={28} className="text-[#00F0FF]" />
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white">
          {isItalian ? 'Il Mio Profilo' : 'My Profile'}
        </h1>
      </motion.div>

      {/* Profile Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0a0d15] border border-white/10  overflow-hidden">
        {/* Profile Picture Section */}
        <div className="p-6 border-b border-white/10">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative group">
              <div className="w-32 h-32 rounded-full bg-[#050508] border-2 border-white/20 flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <Gamepad2 size={48} className="text-[#00F0FF]" />
                )}
              </div>
              <div
                className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera size={32} className="text-white" />
              </div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} className="hidden" data-testid="profile-picture-input" />
            </div>

            <div className="flex-1 text-center sm:text-left">
              <h2 className="font-heading text-2xl font-bold text-white">
                {user?.gamertag || user?.name}
              </h2>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2">
                <span className={`px-2 py-1 text-xs rounded uppercase tracking-wider ${roleBadge.bg} ${roleBadge.text} border ${roleBadge.border}`}>
                  {roleBadge.label}
                </span>
                {user?.playing_position && (
                  <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                    {user.playing_position}
                  </span>
                )}
                {user?.platform && (
                  <span className="px-2 py-1 text-xs rounded bg-[#2979FF]/20 text-[#2979FF] border border-[#2979FF]/30 uppercase tracking-wider" data-testid="profile-platform-badge">
                    🎮 {user.platform}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-4">
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#00F0FF] hover:bg-[#00F0FF]/90" size="sm" data-testid="upload-picture-btn">
                  {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
                  {isItalian ? 'Carica Foto' : 'Upload Photo'}
                </Button>
                {user?.picture && (
                  <Button onClick={handleRemovePicture} disabled={removing} variant="outline" className="border-[#FF1744] text-[#FF1744] hover:bg-[#FF1744]/10" size="sm" data-testid="remove-picture-btn">
                    {removing ? <div className="w-4 h-4 border-2 border-[#FF1744] border-t-transparent rounded-full animate-spin mr-2" /> : <Trash2 size={16} className="mr-2" />}
                    {isItalian ? 'Rimuovi' : 'Remove'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {isItalian ? 'Formati: JPG, PNG, GIF, WEBP. Max 2MB' : 'Formats: JPG, PNG, GIF, WEBP. Max 2MB'}
                {pictureChangeCount >= 1 && (
                  <span className="block text-[#FFEA00] mt-1">
                    <Coins size={12} className="inline mr-1" />
                    {isItalian ? `Costo cambio: ${PICTURE_CHANGE_COST.toLocaleString()} coins` : `Change cost: ${PICTURE_CHANGE_COST.toLocaleString()} coins`}
                  </span>
                )}
                {pictureChangeCount === 0 && (
                  <span className="block text-[#00E676] mt-1">
                    {isItalian ? 'Primo cambio gratuito!' : 'First change free!'}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="p-6 space-y-4">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-400 mb-4">
            {isItalian ? 'Informazioni Profilo' : 'Profile Information'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Gamertag — with change button */}
            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Gamepad2 size={18} className="text-[#00F0FF] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Gamertag</p>
                <p className="text-white font-medium truncate">{user?.gamertag || '-'}</p>
              </div>
              <button
                data-testid="change-gamertag-btn"
                onClick={() => { setNewGamertag(''); setShowGamertagDialog(true); }}
                className="flex-shrink-0 p-1.5 rounded bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] transition-colors"
                title={isItalian ? 'Cambia Gamertag' : 'Change Gamertag'}
              >
                <Pencil size={14} />
              </button>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Mail size={18} className="text-[#00F0FF]" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                <p className="text-white font-medium truncate">{user?.email || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Phone size={18} className="text-[#00F0FF]" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{isItalian ? 'Telefono' : 'Phone'}</p>
                <p className="text-white font-medium">{user?.phone || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Calendar size={18} className="text-[#00F0FF]" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{isItalian ? 'Data Nascita' : 'Birth Date'}</p>
                <p className="text-white font-medium">{user?.birth_date || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Coins size={18} className="text-[#FFEA00]" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{isItalian ? 'Coins Personali' : 'Personal Coins'}</p>
                <p className="text-[#FFEA00] font-mono font-bold">{(user?.personal_coins || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#050508] border border-white/10 ">
              <Shield size={18} className="text-[#00F0FF]" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{isItalian ? 'Ruolo' : 'Role'}</p>
                <p className="text-white font-medium capitalize">{user?.role?.replace('_', ' ') || '-'}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 p-3 bg-[#050508] border border-[#5865F2]/25" data-testid="discord-link-card">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="#5865F2">
                  <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Discord</p>
                  <p className={`font-medium text-sm ${user?.discord_linked ? 'text-[#00E676]' : 'text-slate-400'}`}>
                    {user?.discord_linked
                      ? (isItalian ? 'Collegato ✓' : 'Linked ✓')
                      : (isItalian ? 'Non collegato' : 'Not linked')}
                  </p>
                </div>
              </div>
              {user?.discord_linked ? (
                <Button size="sm" variant="ghost" onClick={handleUnlinkDiscord} className="text-slate-400 hover:text-[#FF1744] text-xs shrink-0" data-testid="discord-unlink-btn">
                  {isItalian ? 'Scollega' : 'Unlink'}
                </Button>
              ) : (
                <Button size="sm" onClick={handleLinkDiscord} className="bg-[#5865F2]/20 border border-[#5865F2]/40 text-[#8b95ff] hover:bg-[#5865F2]/30 text-xs shrink-0" data-testid="discord-link-btn">
                  {isItalian ? 'Collega' : 'Link'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* EA Pro Clubs personal stats */}
      {user?.user_id && (
        <EAPlayerStatsCard
          userId={user.user_id}
          editable={true}
          currentEaGamertag={user?.ea_gamertag}
          isItalian={isItalian}
        />
      )}

      {/* Change Gamertag Dialog */}
      {showGamertagDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowGamertagDialog(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="change-gamertag-dialog"
            className="bg-[#0a0d15] border border-white/10 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Gamepad2 size={20} className="text-[#00F0FF]" />
                {isItalian ? 'Cambia Gamertag' : 'Change Gamertag'}
              </h3>
              <button onClick={() => setShowGamertagDialog(false)} className="text-gray-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Cost warning */}
            <div className={`flex items-start gap-3 p-3 rounded-lg mb-5 ${canAfford ? 'bg-[#FFEA00]/10 border border-[#FFEA00]/20' : 'bg-[#FF1744]/10 border border-[#FF1744]/20'}`}>
              <AlertTriangle size={20} className={canAfford ? 'text-[#FFEA00] flex-shrink-0 mt-0.5' : 'text-[#FF1744] flex-shrink-0 mt-0.5'} />
              <div>
                <p className={`text-sm font-semibold ${canAfford ? 'text-[#FFEA00]' : 'text-[#FF1744]'}`}>
                  {isItalian ? `Costo: ${GAMERTAG_COST.toLocaleString()} coins` : `Cost: ${GAMERTAG_COST.toLocaleString()} coins`}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {isItalian
                    ? `Hai ${(user?.personal_coins || 0).toLocaleString()} coins disponibili`
                    : `You have ${(user?.personal_coins || 0).toLocaleString()} coins available`}
                </p>
                {!canAfford && (
                  <p className="text-xs text-[#FF1744] mt-1 font-semibold">
                    {isItalian ? 'Coins insufficienti!' : 'Not enough coins!'}
                  </p>
                )}
              </div>
            </div>

            {/* Current gamertag */}
            <p className="text-xs text-slate-500 mb-1">{isItalian ? 'Gamertag attuale' : 'Current gamertag'}</p>
            <p className="text-white font-mono font-semibold mb-4">{user?.gamertag || '-'}</p>

            {/* New gamertag input */}
            <label className="text-xs text-slate-500 uppercase tracking-wider block mb-1.5">
              {isItalian ? 'Nuovo Gamertag' : 'New Gamertag'}
            </label>
            <Input
              data-testid="new-gamertag-input"
              value={newGamertag}
              onChange={(e) => setNewGamertag(e.target.value)}
              placeholder={isItalian ? 'Inserisci nuovo gamertag...' : 'Enter new gamertag...'}
              maxLength={20}
              className="bg-[#050508] border-white/10 text-white mb-1"
            />
            <p className="text-xs text-slate-500 mb-5">{isItalian ? 'Min 3, max 20 caratteri' : 'Min 3, max 20 characters'}</p>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowGamertagDialog(false)}
                className="flex-1 border-white/10 text-slate-300 hover:bg-white/5"
              >
                {isItalian ? 'Annulla' : 'Cancel'}
              </Button>
              <Button
                data-testid="confirm-change-gamertag-btn"
                onClick={handleChangeGamertag}
                disabled={!canAfford || changingGamertag || !newGamertag.trim() || newGamertag.trim().length < 3}
                className="flex-1 bg-[#00F0FF] hover:bg-[#00F0FF]/90 disabled:opacity-40"
              >
                {changingGamertag ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Coins size={16} className="mr-2" />
                )}
                {isItalian ? 'Conferma Cambio' : 'Confirm Change'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Picture Cost Confirmation Dialog */}
      {showPictureCostDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setShowPictureCostDialog(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#0a0d15] border border-white/10 rounded-lg p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded bg-[#FFEA00]/20 flex items-center justify-center"><Coins size={20} className="text-[#FFEA00]" /></div>
              <h3 className="font-heading text-lg uppercase tracking-wider text-white">{isItalian ? 'Conferma Cambio' : 'Confirm Change'}</h3>
            </div>
            <div className="p-4 bg-[#050508] border border-[#FFEA00]/20 rounded mb-4">
              <p className="text-white text-center text-sm">
                {isItalian
                  ? `Cambiare l'immagine profilo costa ${PICTURE_CHANGE_COST.toLocaleString()} coins.`
                  : `Changing profile picture costs ${PICTURE_CHANGE_COST.toLocaleString()} coins.`}
              </p>
              <p className="text-xs text-slate-400 text-center mt-2">
                {isItalian ? 'I coins verranno scalati dal tuo saldo personale.' : 'Coins will be deducted from your personal balance.'}
              </p>
              <div className="flex justify-center mt-3">
                <span className="text-sm text-slate-300">{isItalian ? 'Saldo:' : 'Balance:'} <span className={`font-mono font-bold ${(user?.personal_coins || 0) >= PICTURE_CHANGE_COST ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>{(user?.personal_coins || 0).toLocaleString()}</span></span>
              </div>
            </div>
            {(user?.personal_coins || 0) < PICTURE_CHANGE_COST && (
              <div className="p-3 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded mb-4">
                <p className="text-xs text-[#FF1744] text-center flex items-center justify-center gap-1">
                  <AlertTriangle size={14} />{isItalian ? 'Coins insufficienti!' : 'Insufficient coins!'}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-white/10" onClick={() => { setShowPictureCostDialog(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>{isItalian ? 'Annulla' : 'Cancel'}</Button>
              <Button className="flex-1 bg-[#FFEA00] hover:bg-[#FFEA00]/90 text-black font-bold" disabled={uploading || (user?.personal_coins || 0) < PICTURE_CHANGE_COST} onClick={() => { setShowPictureCostDialog(false); if (pendingFile) doUpload(pendingFile); }} data-testid="confirm-picture-cost-btn">
                {uploading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" /> : <Coins size={14} className="mr-2" />}
                {isItalian ? 'Paga e Cambia' : 'Pay & Change'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Profile;
